import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { firestore, addDoc, updateDoc, doc, collection, storage, ref, uploadBytesResumable, getDownloadURL } from './firebase-config';
import { serverTimestamp, getDoc } from 'firebase/firestore';
import './AddEditPost.css';

// Strip empty paragraphs that Quill inserts around blockquotes on load
const cleanForEditor = (html) => {
  if (!html) return html;
  return html
    .replace(/<blockquote[^>]*>/gi, '<blockquote>')
    .replace(/<p><br\s*\/?><\/p>(\s*<blockquote)/gi, '$1')
    .replace(/(<\/blockquote>)\s*<p><br\s*\/?><\/p>/gi, '$1');
};

const AddEditPost = ({ post, requireCategory }) => {
  const [title, setTitle] = useState(post ? post.title : '');
  const [content, setContent] = useState(post ? cleanForEditor(post.content) : '');
  const [category, setCategory] = useState(post ? post.category : '');
  const [footnotes, setFootnotes] = useState(post ? (post.footnotes || []).join('\n') : '');
  const [coverImage, setCoverImage] = useState(post ? post.coverImage || '' : '');
  const [authorName, setAuthorName] = useState(post ? post.authorName || '' : '');
  const [youtubeUrl, setYoutubeUrl] = useState(post ? post.youtubeUrl || '' : '');
  const [galleryImages, setGalleryImages] = useState(post ? post.galleryImages || [] : []);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [galleryUploadProgress, setGalleryUploadProgress] = useState(null);
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const inlineImageInputRef = useRef(null);

  // Upload a file to Firebase Storage and return the download URL
  const uploadToStorage = (file, folder = 'post-images') =>
    new Promise((resolve, reject) => {
      const storageRef = ref(storage, `${folder}/${Date.now()}-${file.name}`);
      const task = uploadBytesResumable(storageRef, file);
      task.on('state_changed', null, reject, async () => {
        resolve(await getDownloadURL(task.snapshot.ref));
      });
    });

  // Insert image URL at current cursor position in Quill
  const insertImageIntoEditor = (url) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection(true);
    quill.insertEmbed(range.index, 'image', url);
    quill.setSelection(range.index + 1);
  };

  // Toolbar image button handler — opens file picker instead of URL prompt
  const handleInlineImageClick = useCallback(() => inlineImageInputRef.current.click(), []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: { image: handleInlineImageClick },
    },
  }), [handleInlineImageClick]);

  const handleInlineImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadToStorage(file, 'post-images');
    insertImageIntoEditor(url);
    e.target.value = '';
  };

  // Paste handler — intercept image files pasted from clipboard
  const handlePaste = async (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    if (!imageItem) return;
    e.preventDefault();
    const file = imageItem.getAsFile();
    const url = await uploadToStorage(file, 'post-images');
    insertImageIntoEditor(url);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const storageRef = ref(storage, `cover-images/${Date.now()}-${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);
    uploadTask.on('state_changed',
      (snapshot) => {
        const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setUploadProgress(pct);
      },
      (error) => { console.error(error); setUploadProgress(null); },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setCoverImage(url);
        setUploadProgress(null);
      }
    );
  };

  const handleGalleryUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    files.forEach(file => {
      const storageRef = ref(storage, `gallery-images/${Date.now()}-${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on('state_changed',
        (snapshot) => {
          const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setGalleryUploadProgress(pct);
        },
        (error) => { console.error(error); setGalleryUploadProgress(null); },
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          setGalleryImages(prev => [...prev, { url, caption: '' }]);
          setGalleryUploadProgress(null);
        }
      );
    });
    e.target.value = '';
  };

  const updateCaption = (index, caption) => {
    setGalleryImages(prev => prev.map((img, i) => i === index ? { ...img, caption } : img));
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
  };

  // Extract YouTube video ID from various URL formats
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setContent(cleanForEditor(post.content));
      setCategory(post.category || '');
      setFootnotes((post.footnotes || []).join('\n'));
      setCoverImage(post.coverImage || '');
      setAuthorName(post.authorName || '');
      setYoutubeUrl(post.youtubeUrl || '');
      setGalleryImages(post.galleryImages || []);
    }
  }, [post]);

  // Attach paste handler to Quill editor DOM node
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const editorEl = quill.root;
    editorEl.addEventListener('paste', handlePaste);
    return () => editorEl.removeEventListener('paste', handlePaste);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requireCategory && !category) {
      alert('Category is required.');
      return;
    }

    const postData = {
      title, content, category, coverImage, authorName, youtubeUrl, galleryImages,
      footnotes: footnotes.split('\n').map(f => f.trim()).filter(Boolean),
      updatedAt: serverTimestamp()
    };

    if (post) {
      // Get the original createdAt timestamp if it exists
      const postDoc = await getDoc(doc(firestore, 'savedclay_posts', post.id));
      const createdAt = postDoc.data()?.createdAt || serverTimestamp();

      await updateDoc(doc(firestore, 'savedclay_posts', post.id), { ...postData, createdAt });
    } else {
      await addDoc(collection(firestore, 'savedclay_posts'), { ...postData, createdAt: serverTimestamp() });
    }
    setTitle(''); setContent(''); setCategory('');
    setCoverImage(''); setAuthorName(''); setYoutubeUrl(''); setGalleryImages([]);
    setFootnotes('');
  };

  return (
    <form onSubmit={handleSubmit} className="add-edit-post">
      <input
        type="text"
        placeholder="Title"
        className='title-input'
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />
      <input
        type="text"
        placeholder="Author full name"
        className='author-input'
        value={authorName}
        onChange={(e) => setAuthorName(e.target.value)}
      />
      <div className="cover-image-field">
        {coverImage && (
          <img src={coverImage} alt="Cover preview" className="cover-preview" />
        )}
        <div className="cover-image-actions">
          <button type="button" className="upload-btn" onClick={() => fileInputRef.current.click()}>
            {coverImage ? 'Change cover image' : 'Upload cover image'}
          </button>
          {coverImage && (
            <button type="button" className="remove-btn" onClick={() => setCoverImage('')}>Remove</button>
          )}
          {uploadProgress !== null && (
            <span className="upload-progress">Uploading... {uploadProgress}%</span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />
      </div>
      <ReactQuill
        ref={quillRef}
        value={content}
        onChange={setContent}
        modules={modules}
        formats={[
          'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
          'list', 'bullet', 'link', 'image'
        ]}
      />
      {/* Hidden file input for inline image toolbar button */}
      <input
        ref={inlineImageInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleInlineImageUpload}
      />
      <input
        type="text"
        placeholder="Categories (comma-separated, e.g. Abortion, Ethics)"
        className='category-input'
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        required={requireCategory}
      />

      {/* Footnotes */}
      <div className="editor-section">
        <label className="editor-section-label">Footnotes (one per line, referenced as [1], [2], etc. in text)</label>
        <textarea
          className="footnotes-input"
          placeholder={"1. Source or comment here\n2. Another footnote"}
          value={footnotes}
          onChange={(e) => setFootnotes(e.target.value)}
          rows={4}
        />
      </div>

      {/* YouTube embed */}
      <div className="editor-section">
        <label className="editor-section-label">YouTube Video (optional)</label>
        <input
          type="url"
          placeholder="Paste YouTube URL e.g. https://youtu.be/abc123"
          className="youtube-input"
          value={youtubeUrl}
          onChange={(e) => setYoutubeUrl(e.target.value)}
        />
        {getYouTubeId(youtubeUrl) && (
          <div className="youtube-preview">
            <iframe
              src={`https://www.youtube.com/embed/${getYouTubeId(youtubeUrl)}`}
              title="YouTube preview"
              allowFullScreen
            />
          </div>
        )}
      </div>

      {/* Gallery images */}
      <div className="editor-section">
        <label className="editor-section-label">Gallery Images (optional)</label>
        <div className="gallery-editor">
          {galleryImages.map((img, i) => (
            <div key={i} className="gallery-editor-item">
              <img src={img.url} alt={`Gallery ${i + 1}`} />
              <input
                type="text"
                placeholder="Caption (optional)"
                value={img.caption}
                onChange={(e) => updateCaption(i, e.target.value)}
                className="gallery-caption-input"
              />
              <button type="button" className="remove-btn" onClick={() => removeGalleryImage(i)}>Remove</button>
            </div>
          ))}
          <div className="gallery-upload-row">
            <button type="button" className="upload-btn" onClick={() => galleryInputRef.current.click()}>
              + Add image(s)
            </button>
            {galleryUploadProgress !== null && (
              <span className="upload-progress">Uploading... {galleryUploadProgress}%</span>
            )}
          </div>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleGalleryUpload}
          />
        </div>
      </div>
      <button type="submit">{post ? 'Update Post' : 'Add Post'}</button>
    </form>
  );
};

export default AddEditPost;
